// v2 baobab — a fluted bottle trunk: a thick tapering core wrapped in five
// vertical buttress lobes rising from splayed root flares to a pinched neck,
// under a crown of four forking, root-like bare branches that carry only sparse
// flat foliage tufts (the leafless "upside-down tree" look), plus a pair of
// dangling seed pods. Variants spin the flutes and re-deal branches, tufts and
// pods. Ported 1:1 from age-of-mcp's v2/tree-baobab.ts, using the library's
// shared `tropic` (h01, lerp, tube, tuft) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part } from "../../lib";

const BARK = "#b59a72";
const BARK_DK = "#9c855e";
const LOBE = "#a38a60";
const LEAF = "#79a048";
const LEAF_2 = "#688b3c";
const POD = "#8a7348";

function baobabParts(v: number): Part[] {
  const seed = v * 23 + 13;
  const yaw = v * 1.9 + tropic.h01(seed, 0) * 0.6;
  const parts: Part[] = [
    // thick tapering core of the bottle trunk
    { geometry: new THREE.CylinderGeometry(0.3, 0.46, 1.6, 9),
      color: BARK, position: [0, 0.8, 0] },
    // pinched neck up to the crown
    { geometry: new THREE.CylinderGeometry(0.14, 0.33, 0.5, 8),
      color: BARK, position: [0, 1.78, 0] },
  ];
  // Five vertical buttress lobes fluting the trunk, tops leaning inward.
  for (let i = 0; i < 5; i++) {
    const a = yaw + (i / 5) * Math.PI * 2;
    parts.push({
      geometry: new THREE.CylinderGeometry(0.1, 0.16, 1.25, 6),
      color: i % 2 ? LOBE : BARK_DK,
      position: [Math.cos(a) * 0.33, 0.63, Math.sin(a) * 0.33],
      rotation: [Math.sin(a) * -0.12, 0, Math.cos(a) * 0.12],
    });
  }
  // Splayed root flares gripping the ground.
  for (let i = 0; i < 4; i++) {
    const a = yaw + 0.6 + (i / 4) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.4;
    parts.push(tropic.tube(
      [Math.cos(a) * 0.3, 0.28, Math.sin(a) * 0.3],
      [Math.cos(a) * 0.56, 0, Math.sin(a) * 0.56],
      0.09, 0.16, BARK_DK, 5));
  }
  // Crown: four forking, root-like bare branches plus one centre twig.
  const tips: Array<readonly [number, number, number]> = [];
  for (let i = 0; i < 4; i++) {
    const a = yaw + 0.35 + (i / 4) * Math.PI * 2 + tropic.h01(seed, 20 + i) * 0.5;
    const mx = Math.cos(a) * 0.4, mz = Math.sin(a) * 0.4;
    parts.push(tropic.tube([0, 1.96, 0], [mx, 2.3, mz], 0.085, 0.045, BARK_DK, 5));
    const ta = a + tropic.lerp(-0.5, 0.5, tropic.h01(seed, 30 + i));
    const tip: readonly [number, number, number] =
      [Math.cos(ta) * 0.58, 2.44, Math.sin(ta) * 0.58];
    parts.push(tropic.tube([mx, 2.28, mz], tip, 0.035, 0.012, BARK_DK, 4));
    tips.push(tip);
  }
  parts.push(tropic.tube([0, 2.0, 0], [0.06, 2.42, -0.05], 0.05, 0.018, BARK_DK, 4));
  tips.push([0.06, 2.44, -0.05]);
  // Sparse flat foliage tufts at the twig tips — the canopy stays thin.
  for (let i = 0; i < 5; i++) {
    const [tx, ty, tz] = tips[i]!;
    parts.push(tropic.tuft(tx, ty + 0.02, tz, tropic.lerp(0.13, 0.17, tropic.h01(seed, 40 + i)),
      0.42, i % 2 ? LEAF : LEAF_2, yaw + i));
  }
  // Two seed pods dangling under the crown.
  for (let i = 0; i < 2; i++) {
    const a = yaw + 1.4 + i * 2.7;
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.05, 0),
      color: POD,
      position: [Math.cos(a) * 0.34, 2.12, Math.sin(a) * 0.34],
      scale: [0.75, 1.35, 0.75] });
  }
  return parts;
}

export const treeBaobabV2 = defineModel({
  id: "tree-baobab-v2",
  batchId: "fable",
  name: "Tree baobab (v2)",
  tags: ["scenery","tree","baobab","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree baobab.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.9,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => baobabParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => baobabParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => baobabParts(2) },
  ],
  animations: [],
});
